import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import {
  getCvAccessToken,
  setupAxios,
  RegisterAuth,
  UserRefreshToken,
  getUserState
} from '@carvana/core-auth';
import { getGraphQLAuthHandler } from '@carvana/stc-utilities';

import { recordGenericEvent, EventActions } from '../analytics/uiMetrics';
import { BASE_URL } from './helpers';

type RetryableConfig = InternalAxiosRequestConfig & { _authNotAuthenticatedRetried?: boolean };

// @carvana/stc-utilities's getGraphQLAuthHandler only refreshes/retries on a GraphQL
// AUTHENTICATION_REQUIRED error code -- the BFF's actual [Authorize] rejection code is
// AUTH_NOT_AUTHENTICATED (see Carvana.STC.BFF.Shared/ErrorHandling/ErrorCodes.cs), which
// always arrives on an HTTP 200, so it's otherwise never refreshed or retried.
export const hasAuthNotAuthenticatedError = (response: AxiosResponse): boolean => {
  const graphQLErrors = response?.data?.errors;
  return (
    Array.isArray(graphQLErrors) &&
    graphQLErrors.some(error => error?.extensions?.code === 'AUTH_NOT_AUTHENTICATED')
  );
};

// By the time a response interceptor runs, config.data has already been through axios's
// transformRequest and is typically a JSON string, not the original object (unlike a
// request interceptor, which sees the pre-transform object).
const getOperationName = (config: InternalAxiosRequestConfig): string | undefined => {
  const { data } = config ?? {};
  if (data && typeof data === 'object') {
    return data.operationName;
  }
  if (typeof data === 'string') {
    try {
      return JSON.parse(data)?.operationName;
    } catch {
      return undefined;
    }
  }
  return undefined;
};

// The retry this handler performs is a brand-new HTTP request/trace -- if it succeeds,
// it's indistinguishable from a request that never had a problem in any BFF-side metric
// (Datadog, Splunk). This event is the only signal that a recovery actually happened.
const recordAuthRefreshOutcome = (
  outcome: 'recovered' | 'gave_up',
  config: InternalAxiosRequestConfig,
  reason?: string
) => {
  recordGenericEvent(
    'API',
    'GraphQLAuthRefresh',
    outcome === 'recovered' ? EventActions.Recovered : EventActions.Errored,
    { operationName: getOperationName(config), reason }
  );
};

export const createAuthNotAuthenticatedHandler =
  (axiosInstance: AxiosInstance) =>
  async (response: AxiosResponse): Promise<AxiosResponse> => {
    const config = response.config as RetryableConfig;

    if (!hasAuthNotAuthenticatedError(response)) {
      return response;
    }

    if (config._authNotAuthenticatedRetried) {
      recordAuthRefreshOutcome('gave_up', config, 'retry_still_failed');
      return response;
    }

    try {
      const { isLoggedIn } = getUserState();
      if (!isLoggedIn) {
        recordAuthRefreshOutcome('gave_up', config, 'not_logged_in');
        return response;
      }

      const refreshSuccess = await UserRefreshToken();
      if (!refreshSuccess) {
        recordAuthRefreshOutcome('gave_up', config, 'refresh_failed');
        return response;
      }

      const freshToken = getCvAccessToken();
      const retryConfig: RetryableConfig = {
        ...config,
        _authNotAuthenticatedRetried: true
      };
      if (freshToken) {
        retryConfig.headers.Authorization = `Bearer ${freshToken}`;
      }

      const retriedResponse = await axiosInstance(retryConfig);
      // If the retry still has the error, the nested pass through this same handler
      // (the _authNotAuthenticatedRetried branch above) already recorded 'gave_up' --
      // don't double-count it here.
      if (!hasAuthNotAuthenticatedError(retriedResponse)) {
        recordAuthRefreshOutcome('recovered', config);
      }
      return retriedResponse;
    } catch {
      // If refresh/retry fails unexpectedly, fall back to the original response
      // instead of turning it into a rejected promise -- let existing handlers deal with it.
      recordAuthRefreshOutcome('gave_up', config, 'exception');
      return response;
    }
  };

RegisterAuth({
  name: 'stc-appraisal',
  environment: process.env.NODE_ENV || 'development'
});

const axiosInstance = axios.create({ baseURL: BASE_URL });

const authenticatedAxios = setupAxios(axiosInstance, {
  addBearerTokenToAllRequests: true
});

authenticatedAxios.interceptors.request.use(async config => {
  if (config.data && typeof config.data === 'object' && config.data.query) {
    try {
      const { isLoggedIn } = getUserState();
      if (isLoggedIn) {
        const currentAccessToken = getCvAccessToken();
        if (!currentAccessToken) {
          const refreshSuccess = await UserRefreshToken();
          if (refreshSuccess) {
            const freshToken = getCvAccessToken();
            if (freshToken) {
              config.headers.Authorization = `Bearer ${freshToken}`;
            }
          }
        }
      }
    } catch {
      // If verification fails, continue with request - let existing handlers deal with it
    }
  }
  return config;
});

const graphQLAuthHandler = getGraphQLAuthHandler(axiosInstance, getCvAccessToken);
authenticatedAxios.interceptors.response.use(graphQLAuthHandler);
authenticatedAxios.interceptors.response.use(createAuthNotAuthenticatedHandler(axiosInstance));

export { authenticatedAxios, axiosInstance };
